import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import saApi, { SA_API } from "../../superadminApi";
import { toast } from "sonner";
import {
  ShieldCheck, LogOut, Building2, Users, CreditCard, Plus, UserPlus, Trash2,
  Pencil, X, CheckCircle2, Crown, Layers, BadgeCheck, Upload, Image as ImageIcon,
  PauseCircle, PlayCircle, Ban, History,
} from "lucide-react";

const PLAN_COLORS = { starter: "#0d6cb8", business: "#7c3aed", enterprise: "#ff7e00" };
const money = (cents, cur) => `${(cents / 100).toFixed(0)} ${(cur || "eur").toUpperCase()}`;
const logoUrl = (code) => `${SA_API}/companies/${code}/logo?t=${Date.now()}`;
const STATUS_LABEL = { active: "Attivo", suspended: "Sospeso", canceled: "Disdetto", none: "Nessun piano", pending: "In attesa" };

const Modal = ({ title, onClose, children, testid }) => (
  <>
    <div className="fixed inset-0 bg-black/60 z-[80]" onClick={onClose} />
    <div className="fixed inset-0 z-[81] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto shadow-2xl" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 h-14 border-b border-gray-100">
          <h3 className="text-[16px] font-black text-[#16345c]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="sa-modal-close"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  </>
);

const Field = ({ label, ...props }) => (
  <label className="block mb-3">
    <span className="block text-[12px] font-semibold text-gray-600 mb-1">{label}</span>
    <input {...props} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] focus:border-[#16345c] outline-none" />
  </label>
);

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [superadmins, setSuperadmins] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newCompany, setNewCompany] = useState(null);
  const [adminFor, setAdminFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [newSa, setNewSa] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      saApi.get("/superadmin/companies"),
      saApi.get("/superadmin/superadmins"),
      saApi.get("/superadmin/plans"),
    ]).then(([c, s, p]) => {
      setCompanies(c.data.companies || []);
      setSuperadmins(s.data.superadmins || []);
      setPlans(p.data.plans || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    saApi.get("/superadmin/me").then((r) => setMe(r.data)).catch(() => navigate("/superadmin/login", { replace: true }));
    load();
  }, [load, navigate]);

  // Ritorno da Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Pagamento annullato");
      window.history.replaceState({}, "", "/superadmin/dashboard");
      return;
    }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await saApi.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Abbonamento attivato con successo!");
          window.history.replaceState({}, "", "/superadmin/dashboard");
          load();
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2000);
      else {
        toast.info("Pagamento in elaborazione, aggiorna tra poco.");
        window.history.replaceState({}, "", "/superadmin/dashboard");
      }
    };
    poll();
  }, [load]);

  const logout = async () => {
    try { await saApi.post("/superadmin/auth/logout"); } catch { /* noop */ }
    navigate("/superadmin/login", { replace: true });
  };

  const managed = companies.filter((c) => !c.builtin);
  const totalEmployees = companies.reduce((s, c) => s + (c.employee_count || 0), 0);
  const activeSubs = managed.filter((c) => c.plan_status === "active").length;

  const doSubscribe = async (companyId, lookup_key) => {
    try {
      const r = await saApi.post(`/superadmin/companies/${companyId}/subscribe`, { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'avvio del pagamento");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1220] flex items-center justify-center text-gray-400">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-[#0b1220]" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="bg-[#111a2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-[#ff7e00]/15 border border-[#ff7e00]/40 flex items-center justify-center"><ShieldCheck size={18} className="text-[#ff7e00]" /></span>
            <div>
              <div className="text-white font-black text-[15px] leading-none">Console Superadmin</div>
              <div className="text-gray-400 text-[11px] mt-0.5">{me?.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="sa-logout-btn" className="flex items-center gap-2 text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-1.5 rounded-lg transition-colors">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { t: "Aziende attive", v: companies.filter((c) => c.active).length, icon: Building2, tid: "sa-stat-companies" },
            { t: "Dipendenti totali", v: totalEmployees, icon: Users, tid: "sa-stat-employees" },
            { t: "Tenant gestiti", v: managed.length, icon: Layers, tid: "sa-stat-tenants" },
            { t: "Abbonamenti attivi", v: activeSubs, icon: CreditCard, tid: "sa-stat-subscriptions" },
          ].map((s) => (
            <div key={s.tid} data-testid={s.tid} className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <s.icon size={20} className="text-[#ff7e00] mb-3" />
              <div className="text-white text-[28px] font-black leading-none">{s.v}</div>
              <div className="text-gray-400 text-[12px] mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        {/* Companies */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-[18px] font-black">Aziende</h2>
          <button onClick={() => setNewCompany({ code: "", label: "", tagline: "", accent: "#0d6cb8" })} data-testid="sa-new-company-btn"
            className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e86f00] text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            <Plus size={16} /> Nuova azienda
          </button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mb-10">
          <table className="w-full text-left">
            <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3">Azienda</th>
                <th className="px-3 py-3">Dipendenti</th>
                <th className="px-3 py-3">Piano</th>
                <th className="px-3 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {companies.map((c) => {
                const plan = plans.find((p) => p.key === c.plan);
                return (
                  <tr key={c.code} data-testid={`sa-company-row-${c.code}`} className="border-t border-white/5">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-[13px] overflow-hidden shrink-0" style={{ backgroundColor: c.accent || "#334155" }}>
                          {c.has_logo ? <img src={logoUrl(c.code)} alt="" className="w-full h-full object-contain bg-white" /> : (c.short || c.label || "?").charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <div className="text-white font-bold flex items-center gap-2">
                            {c.label}
                            {c.builtin && <span className="text-[10px] font-bold bg-white/10 text-gray-300 px-1.5 py-0.5 rounded-full">Predefinita</span>}
                            {!c.active && <span className="text-[10px] font-bold bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded-full">Disattivata</span>}
                          </div>
                          <div className="text-gray-500 text-[11px]">{c.code}{c.tagline ? ` · ${c.tagline}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-gray-300" data-testid={`sa-seats-${c.code}`}>
                      {c.seat_limit == null ? (
                        <span>{c.employee_count} <span className="text-gray-500 text-[11px]">({c.admin_count} admin)</span></span>
                      ) : (() => {
                        const used = c.employee_count || 0, lim = c.seat_limit;
                        const pct = Math.min(100, Math.round((used / Math.max(1, lim)) * 100));
                        const full = used >= lim, near = !full && pct >= 80;
                        const col = full ? "bg-[#c00510]" : near ? "bg-amber-500" : "bg-[#009640]";
                        return (
                          <div className="min-w-[120px]">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`font-bold ${full ? "text-red-300" : "text-white"}`}>{used}/{lim}</span>
                              <span className="text-gray-500 text-[11px]">{c.admin_count} admin</span>
                            </div>
                            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${col}`} style={{ width: `${pct}%` }} data-testid={`sa-seats-fill-${c.code}`} />
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3.5">
                      {c.builtin ? <span className="text-gray-500">—</span> : c.plan_status === "active" ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full text-white" style={{ backgroundColor: PLAN_COLORS[c.plan] || "#334155" }}>
                          <BadgeCheck size={12} /> {plan?.name || c.plan}
                        </span>
                      ) : (c.plan_status === "suspended" || c.plan_status === "canceled") ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full bg-red-500/20 text-red-300">
                          {c.plan_status === "suspended" ? "Sospeso" : "Disdetto"}
                        </span>
                      ) : <span className="text-gray-500 text-[12px]">Nessun piano</span>}
                    </td>
                    <td className="px-3 py-3.5">
                      {c.builtin ? <span className="text-gray-600 text-[12px] block text-right">Sola lettura</span> : (
                        <div className="flex items-center justify-end gap-1.5">
                          <button title="Crea admin" onClick={() => setAdminFor(c)} data-testid={`sa-create-admin-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><UserPlus size={16} /></button>
                          <button title="Gestisci piano" onClick={() => setPlanFor(c)} data-testid={`sa-manage-plan-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><CreditCard size={16} /></button>
                          <button title="Modifica" onClick={() => setEditFor({ ...c })} data-testid={`sa-edit-company-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Pencil size={16} /></button>
                          {c.active && <button title="Disattiva" onClick={() => { if (window.confirm(`Disattivare ${c.label}?`)) saApi.delete(`/superadmin/companies/${c.id}`).then(() => { toast.success("Azienda disattivata"); load(); }); }} data-testid={`sa-deactivate-company-${c.id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={16} /></button>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Superadmins */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Crown size={18} className="text-[#ff7e00]" /> Superadmin</h2>
          <button onClick={() => setNewSa({ email: "", name: "", password: "" })} data-testid="sa-new-superadmin-btn"
            className="flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-200 text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            <Plus size={16} /> Nuovo superadmin
          </button>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-2 mb-10" data-testid="sa-superadmins-list">
          {superadmins.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-white/5">
              <span className="w-8 h-8 rounded-lg bg-[#ff7e00]/15 flex items-center justify-center text-[#ff7e00]"><Crown size={15} /></span>
              <div className="flex-1"><div className="text-white text-[13px] font-bold">{s.name}</div><div className="text-gray-500 text-[11px]">{s.email}</div></div>
            </div>
          ))}
        </div>
      </div>

      {/* New company modal */}
      {newCompany && (
        <Modal title="Nuova azienda" testid="sa-company-modal" onClose={() => setNewCompany(null)}>
          <Field label="Codice (identificativo, senza spazi)" value={newCompany.code} data-testid="sa-company-code" onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value })} placeholder="es. acme" />
          <Field label="Nome azienda" value={newCompany.label} data-testid="sa-company-label" onChange={(e) => setNewCompany({ ...newCompany, label: e.target.value })} placeholder="es. Acme S.p.A." />
          <Field label="Slogan (opzionale)" value={newCompany.tagline} data-testid="sa-company-tagline" onChange={(e) => setNewCompany({ ...newCompany, tagline: e.target.value })} />
          <label className="block mb-4"><span className="block text-[12px] font-semibold text-gray-600 mb-1">Colore</span>
            <input type="color" value={newCompany.accent} data-testid="sa-company-accent" onChange={(e) => setNewCompany({ ...newCompany, accent: e.target.value })} className="w-16 h-9 rounded border border-gray-200" /></label>
          <button data-testid="sa-company-save" onClick={async () => {
            try { await saApi.post("/superadmin/companies", newCompany); toast.success("Azienda creata"); setNewCompany(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Crea azienda</button>
        </Modal>
      )}

      {/* Edit company modal */}
      {editFor && (
        <Modal title={`Modifica ${editFor.label}`} testid="sa-edit-modal" onClose={() => setEditFor(null)}>
          <Field label="Nome azienda" value={editFor.label} onChange={(e) => setEditFor({ ...editFor, label: e.target.value })} />
          <Field label="Slogan" value={editFor.tagline || ""} onChange={(e) => setEditFor({ ...editFor, tagline: e.target.value })} />
          <label className="block mb-4"><span className="block text-[12px] font-semibold text-gray-600 mb-1">Colore aziendale</span>
            <input type="color" value={editFor.accent || "#0d6cb8"} data-testid="sa-edit-accent" onChange={(e) => setEditFor({ ...editFor, accent: e.target.value })} className="w-16 h-9 rounded border border-gray-200" /></label>

          <div className="mb-4">
            <span className="block text-[12px] font-semibold text-gray-600 mb-2">Logo azienda (login e PDF)</span>
            <div className="flex items-center gap-3">
              <span className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {editFor.has_logo ? <img src={logoUrl(editFor.code)} alt="logo" className="w-full h-full object-contain" data-testid="sa-edit-logo-preview" /> : <ImageIcon size={22} className="text-gray-300" />}
              </span>
              <label className="flex items-center gap-2 cursor-pointer bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-3 py-2 rounded-lg">
                <Upload size={15} /> {editFor.has_logo ? "Sostituisci" : "Carica logo"}
                <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="sa-edit-logo-input" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try { await saApi.post(`/superadmin/companies/${editFor.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo caricato"); setEditFor({ ...editFor, has_logo: true }); load(); }
                  catch (err) { toast.error(err?.response?.data?.detail || "Errore logo"); }
                }} />
              </label>
              {editFor.has_logo && <button data-testid="sa-edit-logo-remove" onClick={async () => { await saApi.delete(`/superadmin/companies/${editFor.id}/logo`); toast.success("Logo rimosso"); setEditFor({ ...editFor, has_logo: false }); load(); }} className="text-[#c00510] border border-red-300 hover:bg-red-50 text-[13px] font-semibold px-3 py-2 rounded-lg">Rimuovi</button>}
            </div>
          </div>

          <button data-testid="sa-edit-save" onClick={async () => {
            try { await saApi.put(`/superadmin/companies/${editFor.id}`, { label: editFor.label, tagline: editFor.tagline, accent: editFor.accent }); toast.success("Salvato"); setEditFor(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Salva</button>
        </Modal>
      )}

      {/* Create admin modal */}
      {adminFor && (
        <Modal title={`Nuovo admin · ${adminFor.label}`} testid="sa-admin-modal" onClose={() => setAdminFor(null)}>
          <p className="text-[12px] text-gray-500 mb-4">L'admin accede dal login normale scegliendo l'azienda <b>{adminFor.label}</b>.</p>
          <AdminForm company={adminFor} onDone={() => { setAdminFor(null); load(); }} />
        </Modal>
      )}

      {/* Plan modal */}
      {planFor && (
        <Modal title={`Abbonamento · ${planFor.label}`} testid="sa-plan-modal" onClose={() => setPlanFor(null)}>
          <PlanManager company={planFor} plans={plans} onSubscribe={doSubscribe} onChanged={() => { setPlanFor(null); load(); }} />
        </Modal>
      )}

      {/* New superadmin modal */}
      {newSa && (
        <Modal title="Nuovo superadmin" testid="sa-superadmin-modal" onClose={() => setNewSa(null)}>
          <Field label="Nome" value={newSa.name} data-testid="sa-sa-name" onChange={(e) => setNewSa({ ...newSa, name: e.target.value })} />
          <Field label="Email" type="email" value={newSa.email} data-testid="sa-sa-email" onChange={(e) => setNewSa({ ...newSa, email: e.target.value })} />
          <Field label="Password" type="password" value={newSa.password} data-testid="sa-sa-password" onChange={(e) => setNewSa({ ...newSa, password: e.target.value })} />
          <button data-testid="sa-sa-save" onClick={async () => {
            try { await saApi.post("/superadmin/superadmins", newSa); toast.success("Superadmin creato"); setNewSa(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Crea superadmin</button>
        </Modal>
      )}
    </div>
  );
};

// Gestione piano: stato corrente, sospendi/riattiva/disdici, storico pagamenti, scelta piano
const PlanManager = ({ company, plans, onSubscribe, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const status = company.plan_status;
  const currentPlan = plans.find((p) => p.key === company.plan);

  useEffect(() => {
    saApi.get(`/superadmin/companies/${company.id}/payments`).then((r) => setPayments(r.data.payments || [])).catch(() => {});
  }, [company.id]);

  const act = async (path, label) => {
    try { await saApi.post(`/superadmin/companies/${company.id}/plan/${path}`); toast.success(label); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      {/* Stato corrente */}
      <div className="rounded-xl border border-gray-200 p-4 mb-4" data-testid="sa-plan-current">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide font-bold">Stato abbonamento</div>
            <div className="text-[16px] font-black text-[#16345c]">{currentPlan ? currentPlan.name : "Nessun piano"}
              <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${status === "active" ? "bg-emerald-100 text-emerald-700" : status === "suspended" ? "bg-amber-100 text-amber-700" : status === "canceled" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}>{STATUS_LABEL[status] || "Nessun piano"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {status === "active" && <button data-testid="sa-plan-suspend" onClick={() => act("suspend", "Abbonamento sospeso")} className="flex items-center gap-1 text-[12px] font-bold text-amber-700 border border-amber-300 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg"><PauseCircle size={15} /> Sospendi</button>}
            {status === "suspended" && <button data-testid="sa-plan-resume" onClick={() => act("resume", "Abbonamento riattivato")} className="flex items-center gap-1 text-[12px] font-bold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg"><PlayCircle size={15} /> Riattiva</button>}
            {(status === "active" || status === "suspended") && <button data-testid="sa-plan-cancel" onClick={() => { if (window.confirm("Disdire l'abbonamento? Verrà annullato anche su Stripe.")) act("cancel", "Abbonamento disdetto"); }} className="flex items-center gap-1 text-[12px] font-bold text-[#c00510] border border-red-300 hover:bg-red-50 px-2.5 py-1.5 rounded-lg"><Ban size={15} /> Disdici</button>}
          </div>
        </div>
      </div>

      {/* Scelta / cambio piano */}
      <div className="space-y-3 mb-5">
        {plans.map((p) => {
          const current = company.plan === p.key && status === "active";
          return (
            <div key={p.key} data-testid={`sa-plan-${p.key}`} className="border border-gray-200 rounded-xl p-4" style={{ borderColor: current ? PLAN_COLORS[p.key] : undefined }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-black" style={{ color: PLAN_COLORS[p.key] }}>{p.name}</span>
                  {current && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"><CheckCircle2 size={11} /> Attivo</span>}
                </div>
                <span className="text-[15px] font-black text-[#16345c]">{money(p.amount, p.currency)}<span className="text-[11px] text-gray-400 font-semibold">/mese</span></span>
              </div>
              <ul className="text-[12px] text-gray-500 mb-3 space-y-0.5">{p.features.map((f) => <li key={f}>• {f}</li>)}</ul>
              <button data-testid={`sa-plan-subscribe-${p.key}`} onClick={() => onSubscribe(company.id, p.lookup_key)}
                className="w-full text-white font-bold py-2 rounded-lg text-[13px] transition-opacity hover:opacity-90" style={{ backgroundColor: PLAN_COLORS[p.key] }}>
                {current ? "Rinnova / cambia" : "Attiva con pagamento"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Storico pagamenti */}
      <div data-testid="sa-plan-history">
        <div className="flex items-center gap-2 text-[12px] font-bold text-gray-600 mb-2"><History size={14} /> Storico pagamenti</div>
        {payments.length === 0 ? <p className="text-[12px] text-gray-400">Nessun pagamento registrato.</p> : (
          <div className="space-y-1.5">
            {payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-[12px] border border-gray-100 rounded-lg px-3 py-2">
                <span className="text-gray-600">{p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"} · <b className="text-[#16345c] capitalize">{p.plan || "—"}</b></span>
                <span className="flex items-center gap-2">
                  <span className="text-gray-500">{money(p.amount || 0, p.currency)}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{p.payment_status === "paid" ? "Pagato" : (p.payment_status || "in sospeso")}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Form separato per la creazione admin (stato locale isolato)
const AdminForm = ({ company, onDone }) => {
  const [f, setF] = useState({ matricola: "", email: "", name: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <Field label="Matricola / username" value={f.matricola} data-testid="sa-admin-matricola-input" onChange={set("matricola")} placeholder="es. acmeadmin" />
      <Field label="Nome completo" value={f.name} data-testid="sa-admin-name" onChange={set("name")} />
      <Field label="Email" type="email" value={f.email} data-testid="sa-admin-email" onChange={set("email")} />
      <Field label="Password iniziale" type="text" value={f.password} data-testid="sa-admin-password" onChange={set("password")} />
      <p className="text-[11px] text-gray-500 mb-2">Le credenziali (email + password) verranno inviate automaticamente via email al nuovo admin.</p>
      <button data-testid="sa-admin-save" onClick={async () => {
        try { await saApi.post(`/superadmin/companies/${company.id}/admin`, { ...f, origin_url: window.location.origin }); toast.success("Admin creato — email con le credenziali inviata"); onDone(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
      }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px] mt-1">Crea admin</button>
    </div>
  );
};

export default SuperadminDashboard;
